import { useEffect } from 'react';
import { useState } from 'react';
import Moment from 'moment';
import { PilotAssignmentCommands } from '../../../services/DispatcherActions';
import uuid from 'react-uuid';
import Spacer from '../../../../../components/layout/Spacer';
import { Dialog } from '../../../../../components/layout/dialogs/Dialog';
import { DialogProperties } from '../../../../../components/layout/dialogs/DialogProperties';
import TextBoxFieldWrapper from '../../../../../components/layout/fieldWrappers/TextBoxFieldWrapper';
import DateTimePickerFieldWrapper from '../../../../../components/layout/fieldWrappers/DateTimePickerFieldWrapper';
import SwitchFieldWrapper from '../../../../../components/layout/fieldWrappers/SwitchFieldWrapper';
import TextAreaFieldWrapper from '../../../../../components/layout/fieldWrappers/TextAreaFieldWrapper';
import { deepCopyObject, isNullOrEmpty, isObjectNull } from '../../../../../components/helpers/ObjectHelpers';
import { DialogForm } from '../../../../../components/layout/DialogForm';
import { Error } from '../../../../../components/layout/labels/Error';
import { prepareObjectForApi } from '../../../../../components/helpers/ApiHelpers';
import DropDownListFieldWrapper from 'components/layout/fieldWrappers/DropDownListFieldWrapper';
import PilotagePilotOvertimeActivityTypeIdbRepository from 'repositories/idb/PilotagePilotOvertimeActivityTypeIdbRepository';

const initialState = {
    isEditable: false,
    fromTime: null,
    toTime: null,
    isMinimumTime: false,
    isTwoThirds: false,
    description: "",
    hours: "",
    warnings: [],
    isDirty: false,
    activityType: null,
}

export const OvertimeDialog = ({ onClose, overtime = null, callback, isOvertimeAvailable }) => {

    const [{
        isEditable,
        fromTime,
        toTime,
        isMinimumTime,
        isTwoThirds,
        description,
        hours,
        warnings,
        isDirty,
        activityType,

    }, setState] = useState(initialState);
    const [options, setOptions] = useState([]);

    const setStateValue = (property, e, isDirty = false) => {
        setState((prev) => ({
            ...prev,
            [property]: e,
            isDirty: isDirty
        }));
    }

    const setDescription = (e, isDirty = false) => {
        setStateValue("description", e, isDirty);
    }

    const setIsMinimumTime = (e, isDirty = false) => {
        setStateValue("isMinimumTime", e, isDirty);
    }

    const setIsTwoThirds = (e, isDirty = false) => {
        setStateValue("isTwoThirds", e, isDirty);
    }

    const setHours = (e, isDirty = false) => {
        setStateValue("hours", e, isDirty);
    }

    const setFromTime = (e, isDirty = false) => {
        setStateValue("fromTime", e, isDirty);
    }

    const setToTime = (e, isDirty = false) => {
        setStateValue("toTime", e, isDirty);
    }

    const setWarnings = (e) => {
        setStateValue("warnings", e);
    }

    const setIsEditable = (e) => {
        setStateValue("isEditable", e);
    }

    const setActivityType = (e, isDirty = false) => {
        setStateValue("activityType", e, isDirty);
    }

    useEffect(() => {
        initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isDirty) return;
        const calculationResult = computeHours({ fromTime, toTime, isMinimumTime, isTwoThirds });
        setHours(calculationResult.hours);
        setWarnings(calculationResult.warnings);
        validateInputs({ fromTime, toTime, activityType, warnings: calculationResult.warnings });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromTime, toTime, isMinimumTime, isTwoThirds, description, activityType]);

    return (
        <Dialog
            properties={{
                ...DialogProperties,
                title: "Overtid",
                onClose: onClose,
                actions: [
                    {
                        onClick: onClose,
                        icon: "close",
                        text: "Avbryt"
                    },
                    {
                        onClick: onSaveClick,
                        disabled: !isEditable,
                        icon: "ok",
                        themeColor: "primary",
                        text: "Ok"
                    }
                ]
            }}>
            <DialogForm>
                {
                    !isOvertimeAvailable &&
                    <>
                        <Error>
                            <p>
                                Merk at losformidler verken har krysset av for overtid, reise overtid eller &gt; 12t arb tid.
                                Er det riktig &aring; rapportere overtid relatert til losoppdraget?
                            </p>
                        </Error>
                    </>
                }

                <DropDownListFieldWrapper
                    title="Type"
                    adaptiveTitle="Velg type"
                    data={options}
                    dataItemKey="pilotagePilotOvertimeActivityTypeId"
                    textField="name"
                    adaptive={true}
                    value={activityType}
                    onChange={onOvertimeTypeChange}
                />

                <Spacer height={20} />

                <DateTimePickerFieldWrapper
                    title="Fra dato"
                    value={fromTime}
                    onChange={onFromTimeChange}
                />

                <Spacer height={20} />

                <DateTimePickerFieldWrapper
                    title="Til dato"
                    value={toTime}
                    onChange={onToTimeChange}
                    warnings={warnings}
                />

                <Spacer height={20} />


                <SwitchFieldWrapper
                    title="Minimumstid 2t"
                    checked={isMinimumTime}
                    onChange={onMinimumTimeChange}
                />

                <Spacer height={20} />

                <SwitchFieldWrapper
                    title="2/3 timer"
                    checked={isTwoThirds}
                    onChange={onTwoThirdshange}
                />

                <Spacer height={20} />

                <TextBoxFieldWrapper
                    title="Antall"
                    value={hours}
                    disabled={true}
                    readOnly={true}
                />

                <Spacer height={20} />

                <TextAreaFieldWrapper
                    title="Beskrivelse"
                    rows={3}
                    value={description}
                    onChange={onDescriptionChange}
                />
            </DialogForm>
        </Dialog>
    )

    async function initialize() {
        const overtimeOptions = await PilotagePilotOvertimeActivityTypeIdbRepository.getAllAsync();
        setOptions(overtimeOptions);

        if (isObjectNull(overtime)) return;

        setDescription(overtime.description);
        setIsMinimumTime(overtime.isMinimum);
        setIsTwoThirds(overtime.isTwoThird);
        setHours(overtime.number);
        setFromTime(Moment(overtime.fromTime).toDate());
        setToTime(Moment(overtime.toTime).toDate());
        setActivityType(overtimeOptions.find(o => o.systemName === overtime.pilotageOvertimeActivityType));
    }

    function onSaveClick() {
        if (!isEditable) return;

        let compensation = {
            guid: uuid(),
            pilotagePilotOvertimeId: 0,
            command: PilotAssignmentCommands.AddPilotAssignmentOvertime
        };

        if (!isObjectNull(overtime)) {
            compensation = deepCopyObject(overtime);
            compensation.command = PilotAssignmentCommands.AddPilotAssignmentOvertime;
            compensation.pilotagePilotOvertimeId = overtime.pilotagePilotOvertimeId;

            if (Object.hasOwn(compensation, "pilotagePilotOvertimeId")) {
                if (compensation.pilotagePilotOvertimeId > 0) {
                    compensation.command = PilotAssignmentCommands.UpdatePilotAssignmentOvertime;
                }
            }

            if (isNullOrEmpty(compensation.guid)) {
                compensation.guid = uuid();
            }
        }

        compensation.fromTime = fromTime;
        compensation.toTime = toTime;
        compensation.isMinimum = isMinimumTime;
        compensation.isTwoThird = isTwoThirds;
        compensation.number = Number(hours);
        compensation.description = description;
        compensation.pilotageOvertimeActivityType = activityType.systemName;

        prepareObjectForApi(compensation);

        callback(compensation);
        onClose();
    }

    function onDescriptionChange(e) {
        setDescription(e.value, true);
    }

    function onFromTimeChange(e) {
        setFromTime(e, true);
    }

    function onToTimeChange(e) {
        setToTime(e, true);
    }

    function onMinimumTimeChange(e) {
        setIsMinimumTime(e.target.value, true);
    }

    function onTwoThirdshange(e) {
        setIsTwoThirds(e.target.value, true);
    }

    function onOvertimeTypeChange(e) {
        setActivityType(e.target.value, true);
    }

    function computeHours({ fromTime, toTime, isMinimumTime, isTwoThirds }) {
        const warnings = [];
        let resultHours = "";

        if (isNullOrEmpty(fromTime) || isNullOrEmpty(toTime)) {
            return { hours: "", warnings };
        }

        const from = Moment(fromTime);
        const to = Moment(toTime);

        if (!from.isValid() || !to.isValid()) {
            warnings.push("Ugyldig datoformat.");
            return { hours: "", warnings };
        }

        if (!from.isBefore(to)) {
            warnings.push("Til-dato kan ikke v&aelig;re f&oslash;r eller lik fra-dato.");
            return { hours: "", warnings };
        }

        const diff = to.diff(from, 'minutes');
        let hours = (diff / 60);

        if (isMinimumTime) {
            hours = 2;
        }
        if (isTwoThirds) {
            hours = (hours / 3) * 2;
        }

        // Guard against negative or zero hours
        if (!(typeof hours === 'number') || hours <= 0) {
            warnings.push("Beregnet antall timer er ugyldig.");
            return { hours: "", warnings };
        }

        resultHours = hours.toFixed(2);

        return { hours: resultHours, warnings };
    }

    function validateInputs({ fromTime, toTime, activityType, warnings = [] }) {
        let editable = true;

        if (isNullOrEmpty(activityType)) {
            editable = false;
        }

        if (isNullOrEmpty(fromTime) || isNullOrEmpty(toTime)) {
            editable = false;
        }

        if (editable) {
            const fromMoment = Moment(fromTime);
            const toMoment = Moment(toTime);

            if (!fromMoment.isValid() || !toMoment.isValid()) {
                editable = false;
            } else if (!fromMoment.isBefore(toMoment)) {
                editable = false;
            }
        }

        // If there are warnings from calculation, don't allow save
        if (warnings && warnings.length > 0) {
            editable = false;
        }

        setIsEditable(editable);
    }
}
